import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { fmtDateWith, fmtTimeRange, toIsoDate } from '../../utils/format';

// Hour grid in the style of Plankton's time tracking week view.
export const HOUR_PX = 48;
const DAY_MIN = 24 * 60;
const SNAP = 15;
const DEFAULT_MIN = 60;      // length of a slot without an end time, and of a new slot
const SCROLL_TO_HOUR = 7;
const GUTTER = 52;
const COL_MIN = 118;

export const toMin = (hhmm) => { const [h, m] = hhmm.split(':').map(Number); return h * 60 + m; };
export const toHhmm = (min) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
const snap = (min) => Math.round(min / SNAP) * SNAP;

// Releasing a resize over an empty hour fires a click there; ignore clicks right after a resize.
let lastResizeEnd = 0;

/** Start/end minutes of a timed job (no end = DEFAULT_MIN long, shown with an open bottom). */
const span = (j) => {
    const start = toMin(j.scheduledStart);
    const end = j.scheduledEnd ? toMin(j.scheduledEnd) : Math.min(start + DEFAULT_MIN, DAY_MIN);
    return { start, end };
};

/** Side-by-side lanes for overlapping jobs: each job gets a lane and the lane count of its overlap group. */
function layoutDay(jobs) {
    const items = jobs.map(j => ({ job: j, ...span(j) })).sort((a, b) => a.start - b.start || a.end - b.end);
    const out = [];
    let group = [];
    let groupEnd = -1;
    const flush = () => {
        const lanes = [];
        group.forEach(it => {
            let lane = lanes.findIndex(end => end <= it.start);
            if (lane < 0) { lane = lanes.length; lanes.push(0); }
            lanes[lane] = it.end;
            it.lane = lane;
        });
        group.forEach(it => out.push({ ...it, lanes: lanes.length }));
        group = [];
    };
    items.forEach(it => {
        if (group.length && it.start >= groupEnd) flush();
        group.push(it);
        groupEnd = Math.max(groupEnd, it.end);
    });
    if (group.length) flush();
    return out;
}

function Block({ item, onResizeEnd, onOpen }) {
    const { job, lane, lanes } = item;
    const [previewEnd, setPreviewEnd] = useState(null);
    const end = previewEnd ?? item.end;
    const height = Math.max(((end - item.start) / 60) * HOUR_PX, 22);
    const color = job.category?.color || '#64748B';
    const done = job.fieldDone;

    // Drag the bottom edge to change the end time (snapped to 15 min).
    const startResize = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const y0 = e.clientY;
        let next = item.end;
        const move = (ev) => {
            next = Math.min(DAY_MIN, Math.max(item.start + SNAP, snap(item.end + ((ev.clientY - y0) / HOUR_PX) * 60)));
            setPreviewEnd(next);
        };
        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            setPreviewEnd(null);
            lastResizeEnd = Date.now();
            if (next !== item.end || !job.scheduledEnd) onResizeEnd(job, item.start, next);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };

    const narrow = lanes > 1;
    const info = [fmtTimeRange(job), job.number, job.title, job.client?.name].filter(Boolean).join(' · ');
    return (
        <Tooltip title={info} placement="right" enterDelay={400} disableInteractive>
        <Box
            draggable
            onDragStart={(e) => {
                // Remember where in the block it was grabbed, so the drop keeps that point under the cursor.
                const grab = ((e.clientY - e.currentTarget.getBoundingClientRect().top) / HOUR_PX) * 60;
                e.dataTransfer.setData('text/plain', JSON.stringify({ id: job.id, grab, length: item.end - item.start }));
                e.dataTransfer.effectAllowed = 'move';
            }}
            onClick={(e) => { e.stopPropagation(); onOpen(job, e.currentTarget); }}
            sx={{
                position: 'absolute', top: (item.start / 60) * HOUR_PX, height,
                left: `calc(${(lane / lanes) * 100}% + 2px)`, width: `calc(${100 / lanes}% - 4px)`,
                bgcolor: done ? '#F8FAFC' : `${color}1F`, borderRadius: '6px',
                border: `1px solid ${color}40`, borderLeft: `3px solid ${color}`, overflow: 'hidden', cursor: 'grab', zIndex: 2,
                opacity: done ? 0.7 : 1, boxSizing: 'border-box',
                borderBottomStyle: job.scheduledEnd || previewEnd ? 'solid' : 'dashed',
                '&:hover': { boxShadow: '0 6px 16px -8px rgba(30,58,82,0.45)', zIndex: 3 },
            }}>
            <Box sx={{ px: 0.6, pt: 0.25 }}>
                <Typography noWrap sx={{ fontSize: narrow ? 9.5 : 10.5, fontWeight: 700, color: '#475569', lineHeight: 1.35, textOverflow: 'clip' }}>
                    {previewEnd !== null ? `${job.scheduledStart}–${toHhmm(previewEnd)}` : fmtTimeRange(job)}
                </Typography>
                <Typography sx={{
                    fontSize: narrow ? 10.5 : 11.5, fontWeight: 700, color: '#0F172A', lineHeight: 1.25, textDecoration: done ? 'line-through' : 'none',
                    display: '-webkit-box', WebkitLineClamp: Math.max(1, Math.floor((height - 18) / 14)), WebkitBoxOrient: 'vertical',
                    overflow: 'hidden', wordBreak: 'break-word',
                }}>
                    {done && <CheckCircleIcon sx={{ fontSize: 12, color: '#059669', mr: 0.3, verticalAlign: '-2px' }} />}
                    {job.title}
                </Typography>
                {height > 56 && !narrow && <Typography noWrap sx={{ fontSize: 10.5, color: '#64748B' }}>{job.client?.name}</Typography>}
            </Box>
            <Box onPointerDown={startResize} onClick={(e) => e.stopPropagation()} draggable={false}
                 onDragStart={(e) => { e.preventDefault(); e.stopPropagation(); }}
                 sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 7, cursor: 'ns-resize', touchAction: 'none' }} />
        </Box>
        </Tooltip>
    );
}

/** Untimed job on a day ("bez termina" strip). */
function DayChip({ job, onOpen }) {
    const color = job.category?.color || '#64748B';
    return (
        <Box draggable
             onDragStart={(e) => { e.dataTransfer.setData('text/plain', JSON.stringify({ id: job.id, grab: 0, length: DEFAULT_MIN })); e.dataTransfer.effectAllowed = 'move'; }}
             onClick={(e) => onOpen(job, e.currentTarget)}
             sx={{
                 display: 'flex', alignItems: 'center', gap: 0.25, px: 0.6, py: 0.2, borderRadius: '5px', cursor: 'grab',
                 bgcolor: job.fieldDone ? '#F1F5F9' : `${color}1F`, borderLeft: `3px solid ${color}`, opacity: job.fieldDone ? 0.7 : 1,
             }}>
            {job.fieldDone && <CheckCircleIcon sx={{ fontSize: 12, color: '#059669' }} />}
            <Typography noWrap sx={{ fontSize: 11, fontWeight: 600, color: '#0F172A', flex: 1, textDecoration: job.fieldDone ? 'line-through' : 'none' }}>
                {job.title}
            </Typography>
        </Box>
    );
}

/** Reads the drag payload set by blocks, chips and the side-list cards (which only set the job id). */
export const readDrag = (e) => {
    const raw = e.dataTransfer.getData('text/plain');
    try {
        const v = JSON.parse(raw);
        if (v && typeof v === 'object') return v;
        return { id: Number(v), grab: 0, length: DEFAULT_MIN };
    } catch {
        return { id: Number(raw), grab: 0, length: DEFAULT_MIN };
    }
};

/**
 * Monday–Sunday hour grid. `jobs` are the week's scheduled jobs. Drop on an hour sets date + time (keeping the
 * slot's length), drop on the top strip sets the date without a time, the bottom edge resizes, clicking an empty
 * hour creates a job there, clicking a job opens its quick view (`onOpen(job, element)`).
 */
export default function WeekGrid({ days, jobs, todayIso, onSchedule, onCreate, onOpen }) {
    const { t } = useTranslation();
    const scrollRef = useRef(null);
    const [over, setOver] = useState(null); // { day, min } or { day, allDay: true }
    const [now, setNow] = useState(() => new Date());

    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = SCROLL_TO_HOUR * HOUR_PX - 10; }, []);
    useEffect(() => { const id = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(id); }, []);

    const byDay = useMemo(() => {
        const m = {};
        days.forEach(d => { m[toIsoDate(d)] = { timed: [], allDay: [] }; });
        jobs.forEach(j => {
            const slot = m[j.scheduledDate];
            if (!slot) return;
            (j.scheduledStart ? slot.timed : slot.allDay).push(j);
        });
        Object.values(m).forEach(s => { s.layout = layoutDay(s.timed); });
        return m;
    }, [days, jobs]);

    const minuteAt = (e, el) => {
        const y = e.clientY - el.getBoundingClientRect().top;
        return Math.max(0, Math.min(DAY_MIN - SNAP, snap((y / HOUR_PX) * 60)));
    };

    const dropOnHour = (e, day) => {
        e.preventDefault();
        setOver(null);
        const { id, grab = 0, length = DEFAULT_MIN } = readDrag(e);
        if (!id) return;
        const len = Math.max(SNAP, Math.min(length, DAY_MIN));
        const start = Math.max(0, Math.min(DAY_MIN - len, snap(minuteAt(e, e.currentTarget) - grab)));
        onSchedule(id, day, toHhmm(start), start + len >= DAY_MIN ? '23:59' : toHhmm(start + len));
    };

    const cols = `${GUTTER}px repeat(7, minmax(${COL_MIN}px, 1fr))`;
    const nowMin = now.getHours() * 60 + now.getMinutes();

    return (
        <Box sx={{ bgcolor: '#fff', border: '1px solid #E2E8F0', borderRadius: '14px', overflowX: 'auto' }}>
            <Box sx={{ minWidth: GUTTER + 7 * COL_MIN }}>
                {/* Day headers + the strip for jobs with a date but no time. Same scrollbar gutter as the body below. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: cols, overflowY: 'hidden', scrollbarGutter: 'stable', borderBottom: '1px solid #EEF2F6' }}>
                    <Box />
                    {days.map(d => {
                        const iso = toIsoDate(d);
                        const today = iso === todayIso;
                        const weekend = d.getDay() === 0 || d.getDay() === 6;
                        return (
                            <Box key={iso} sx={{ py: 1, textAlign: 'center', borderLeft: '1px solid #F1F5F9', bgcolor: weekend ? '#FAFBFC' : 'transparent' }}>
                                <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.6, color: today ? '#427898' : '#94A3B8' }}>
                                    {fmtDateWith(d, { weekday: 'short' })}
                                </Typography>
                                <Box sx={{
                                    mx: 'auto', mt: 0.25, width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    bgcolor: today ? '#427898' : 'transparent', color: today ? '#fff' : '#1a2332', fontWeight: 700, fontSize: 14,
                                }}>{d.getDate()}</Box>
                            </Box>
                        );
                    })}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pr: 0.75, borderTop: '1px solid #F1F5F9' }}>
                        <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#B0BEC5', textAlign: 'right', lineHeight: 1.1 }}>{t('planner.noTime')}</Typography>
                    </Box>
                    {days.map(d => {
                        const iso = toIsoDate(d);
                        const hot = over?.day === iso && over.allDay;
                        return (
                            <Box key={iso}
                                 onDragOver={(e) => { e.preventDefault(); if (!hot) setOver({ day: iso, allDay: true }); }}
                                 onDragLeave={() => setOver(null)}
                                 onDrop={(e) => { e.preventDefault(); setOver(null); const { id } = readDrag(e); if (id) onSchedule(id, iso, null, null); }}
                                 sx={{
                                     minHeight: 34, p: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4,
                                     borderLeft: '1px solid #F1F5F9', borderTop: '1px solid #F1F5F9',
                                     bgcolor: hot ? 'rgba(66,120,152,0.10)' : 'transparent',
                                 }}>
                                {byDay[iso].allDay.map(j => <DayChip key={j.id} job={j} onOpen={onOpen} />)}
                            </Box>
                        );
                    })}
                </Box>

                {/* Hours */}
                <Box ref={scrollRef} sx={{ height: 'max(420px, calc(100vh - 330px))', overflowY: 'auto', scrollbarGutter: 'stable' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: cols, position: 'relative' }}>
                        <Box>
                            {[...Array(24)].map((_, h) => (
                                <Box key={h} sx={{ height: HOUR_PX, position: 'relative' }}>
                                    {h > 0 && (
                                        <Typography sx={{ position: 'absolute', top: -7, right: 8, fontSize: 10, fontWeight: 600, color: '#B0BEC5' }}>
                                            {toHhmm(h * 60)}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                        {days.map(d => {
                            const iso = toIsoDate(d);
                            const weekend = d.getDay() === 0 || d.getDay() === 6;
                            const hot = over?.day === iso && !over.allDay ? over.min : null;
                            return (
                                <Box key={iso}
                                     onDragOver={(e) => {
                                         e.preventDefault();
                                         const min = minuteAt(e, e.currentTarget);
                                         if (over?.day !== iso || over.min !== min) setOver({ day: iso, min });
                                     }}
                                     onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); }}
                                     onDrop={(e) => dropOnHour(e, iso)}
                                     onClick={(e) => {
                                         if (Date.now() - lastResizeEnd < 400) return;
                                         // Empty hour: new job there, one hour long (half-hour precision).
                                         const start = Math.min(DAY_MIN - DEFAULT_MIN, Math.floor(minuteAt(e, e.currentTarget) / 30) * 30);
                                         onCreate(iso, toHhmm(start), toHhmm(start + DEFAULT_MIN));
                                     }}
                                     sx={{
                                         position: 'relative', height: 24 * HOUR_PX, borderLeft: '1px solid #F1F5F9', cursor: 'copy',
                                         bgcolor: weekend ? '#FAFBFC' : 'transparent',
                                         backgroundImage: `repeating-linear-gradient(to bottom, #EEF2F6 0, #EEF2F6 1px, transparent 1px, transparent ${HOUR_PX}px)`,
                                     }}>
                                    {hot !== null && (
                                        <Box sx={{ position: 'absolute', left: 2, right: 2, top: (hot / 60) * HOUR_PX, height: HOUR_PX, borderRadius: '6px',
                                                   bgcolor: 'rgba(66,120,152,0.12)', border: '1px dashed #427898', pointerEvents: 'none' }}>
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#427898', px: 0.5 }}>{toHhmm(hot)}</Typography>
                                        </Box>
                                    )}
                                    {byDay[iso].layout.map(item => (
                                        <Block key={item.job.id} item={item} onOpen={onOpen}
                                               onResizeEnd={(job, start, end) => onSchedule(job.id, iso, toHhmm(start), end >= DAY_MIN ? '23:59' : toHhmm(end))} />
                                    ))}
                                    {iso === todayIso && (
                                        <Box sx={{ position: 'absolute', left: 0, right: 0, top: (nowMin / 60) * HOUR_PX, display: 'flex', alignItems: 'center', zIndex: 4, pointerEvents: 'none' }}>
                                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#EF4444', ml: '-4px' }} />
                                            <Box sx={{ flex: 1, height: '1.5px', bgcolor: '#EF4444' }} />
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
